/**
 * useBrandConfig Hook
 * Manages brand configuration data with hierarchical scope support
 * Fetches from database (rule_fragments table) using config service
 */

import { isSupportedLanguage } from '@ophelos/shared/utils/languages';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { brandConfigKeys } from '@/apps/library/hooks/useBrandConfig';
import { configScopeIds, getScopeType } from '@/apps/settings/hooks/useResetToOperatorDefaults';
import { configEntityId } from '@/lib/debt-type-cohort';
import { contentConfigToBrand, journeyConfigToBrand, themeConfigToBrand } from '@/lib/design/brand-config';
import { deepMerge } from '@/lib/schema-utils';
import type { BrandConfiguration } from '@/lib/types/brand-config';
import { DEFAULT_BRAND_CONFIG } from '@/lib/types/brand-config';
import { getCompiledConfig, getRuleFragment, saveConfig } from '@/services/config';
import { type Client, type DebtType, Domain, type Operator } from '@/types';
import type { ContentConfig } from '@/types/config/content';
import type { JourneyConfig } from '@/types/config/journey';
import type { ThemeConfig } from '@/types/config/theme';

/**
 * Build scope key for config lookup
 * Structure: operator > client > debt_type (debt type sits within client)
 */
function buildScopeKey(operatorId: string, clientId?: string, debtTypeId?: string): string {
  if (debtTypeId) {
    return `${operatorId}:${debtTypeId}`;
  }
  if (clientId) {
    return `${operatorId}:${clientId}`;
  }
  return operatorId;
}

/**
 * Extract theme-related fields from BrandConfiguration
 */
function extractThemeConfig(config: Partial<BrandConfiguration>): ThemeConfig {
  const themeConfig: ThemeConfig = {};

  if (config.theme) {
    themeConfig.theme = config.theme;
  }

  if (config.showGradientSphere !== undefined) {
    themeConfig.showGradientSphere = config.showGradientSphere;
  }

  if (config.defaultLanguage) {
    themeConfig.defaultLanguage = config.defaultLanguage;
  }

  if (config.availableLanguages && config.availableLanguages.length > 0) {
    themeConfig.availableLanguages = config.availableLanguages.filter((lang) => isSupportedLanguage(lang));
  }

  if (config.uiFeatures) {
    themeConfig.features = {
      enableChat: config.uiFeatures.enableChat,
      showDisputes: config.uiFeatures.showDisputes,
      showDocuments: config.uiFeatures.showDocuments,
      showAccountBalance: config.uiFeatures.showAccountBalance,
      showPaymentHistory: config.uiFeatures.showPaymentHistory,
    };
  }

  // Handle logo_full - only include if both light and dark are present
  if (config.logo_full?.light && config.logo_full.dark) {
    themeConfig.logo_full = {
      light: config.logo_full.light,
      dark: config.logo_full.dark,
    };
  }

  // Handle logo_icon - only include if both light and dark are present
  if (config.logo_icon?.light && config.logo_icon.dark) {
    themeConfig.logo_icon = {
      light: config.logo_icon.light,
      dark: config.logo_icon.dark,
    };
  }

  // Handle favicon - only include if both light and dark are present
  if (config.favicon?.light && config.favicon.dark) {
    themeConfig.favicon = {
      light: config.favicon.light,
      dark: config.favicon.dark,
    };
  }

  // Handle colors - only include if brand color exists
  if (config.colors) {
    themeConfig.colors = {
      brand: config.colors.brand,
    };

    // Button background - only include if both light and dark are present
    if (config.colors.buttonBackground?.light && config.colors.buttonBackground.dark) {
      themeConfig.colors.buttonBackground = {
        light: config.colors.buttonBackground.light,
        dark: config.colors.buttonBackground.dark,
      };
    }

    // Button text - only include if both light and dark are present
    if (config.colors.buttonText?.light && config.colors.buttonText.dark) {
      themeConfig.colors.buttonText = {
        light: config.colors.buttonText.light,
        dark: config.colors.buttonText.dark,
      };
    }

    // Links color - handle the fact that BrandConfiguration only has light
    // Use the light value for both light and dark modes if only light is present
    if (config.colors.linksColor?.light) {
      themeConfig.colors.linksColor = {
        light: config.colors.linksColor.light,
      };
    }

    // Email background - handle the fact that BrandConfiguration only has light
    // Use the light value for both light and dark modes if only light is present
    if (config.colors.emailBackground?.light) {
      themeConfig.colors.emailBackground = {
        light: config.colors.emailBackground.light,
      };
    }
  }

  // Handle layout
  if (config.layout?.borderRadius) {
    themeConfig.layout = {
      borderRadius: config.layout.borderRadius,
    };
  }

  return themeConfig;
}

/**
 * Extract content-related fields from BrandConfiguration
 */
function extractContentConfig(config: Partial<BrandConfiguration>): Partial<ContentConfig> {
  return {
    company_info: {
      company_name: config.company_name || '',
      company_phone: config.company_phone || '',
      company_email: config.company_email || '',
      company_address: config.company_address || '',
      support_url: config.support_url || '',
      privacy_policy_url: config.privacy_policy_url || '',
      terms_url: config.terms_url || '',
      unsubscribe_url_template: config.unsubscribe_url_template || '',
    },
    footer_links: config.journey_footer_links || [],
  };
}

/**
 * Extract journey-related fields from BrandConfiguration
 */
function extractJourneyConfig(config: Partial<BrandConfiguration>): Partial<JourneyConfig> {
  return {
    payment_settings: config.payment,
    payment_plan_tiers: config.paymentPlanTiers,
  };
}

export interface UseBrandConfigReturn {
  /**
   * Load config for Hub context entities (returns merged config)
   * Fetches from database and merges hierarchically
   * @param bypassCache - If true, skip cache lookup and fetch fresh data from DB
   */
  loadConfigForContext: (
    operator: Operator | null,
    debtType: DebtType | null,
    client: Client | null,
    bypassCache?: boolean
  ) => Promise<BrandConfiguration>;

  /**
   * Save config for Hub context entities and refresh compiled config
   * @returns The freshly compiled config after save
   */
  saveConfigForContext: (
    operator: Operator | null,
    debtType: DebtType | null,
    client: Client | null,
    config: Partial<BrandConfiguration>
  ) => Promise<BrandConfiguration>;

  /**
   * In-memory config storage (for dev session)
   */
  configs: Record<string, Partial<BrandConfiguration>>;
}

/**
 * Hook for managing brand configuration data
 * Fetches from database using config service
 */
export function useBrandConfig(): UseBrandConfigReturn {
  const queryClient = useQueryClient();
  // Initialize with empty configs
  const [configs, setConfigs] = useState<Record<string, Partial<BrandConfiguration>>>({});
  const [_isLoading, setIsLoading] = useState(false);

  // Fetch brand configs from database on mount
  useEffect(() => {
    async function fetchBrandConfigs() {
      try {
        setIsLoading(true);

        // For now, we'll fetch configs for a default operator
        // In a real app, this would be based on the current user's context
        // This is a temporary approach until we have proper entity selection

        // Initialize with empty config until proper entity selection is implemented
        // The pages using this hook will need to be updated to handle empty initial state
        setConfigs({});
      } catch (error) {
        console.error('Error fetching brand configs:', error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchBrandConfigs();
  }, []);

  /**
   * Load config for Hub context entities
   * Uses compiled_config_for which handles hierarchical merging automatically
   * Structure: system → operator → client → debt_type (most specific wins)
   */
  const loadConfigForContext = useCallback(
    async (
      operator: Operator | null,
      debtType: DebtType | null,
      client: Client | null,
      bypassCache = false
    ): Promise<BrandConfiguration> => {
      // Start with default config
      let merged: BrandConfiguration = { ...DEFAULT_BRAND_CONFIG };

      if (!operator) {
        return merged;
      }

      // Check cache first (unless bypassing)
      const scopeKey = buildScopeKey(operator.id, client?.id, debtType?.id);
      const cachedConfig = configs[scopeKey];
      if (!bypassCache && cachedConfig !== undefined) {
        return deepMerge(merged, cachedConfig);
      }

      // Determine most specific entity ID (debt_type > client > operator)
      const entityId = configEntityId(debtType, client, operator.id);

      try {
        // Fetch compiled configs for each domain (already hierarchically merged by DB)
        const [themeConfig, contentConfig, journeyConfig] = await Promise.all([
          getCompiledConfig(entityId, Domain.THEME),
          getCompiledConfig(entityId, Domain.CONTENT),
          getCompiledConfig(entityId, Domain.JOURNEY),
        ]);

        // Merge compiled configs into brand config
        if (themeConfig?.config_json) {
          merged = deepMerge(merged, themeConfigToBrand(themeConfig.config_json as ThemeConfig));
        }
        if (contentConfig?.config_json) {
          merged = deepMerge(merged, contentConfigToBrand(contentConfig.config_json as ContentConfig));
        }
        if (journeyConfig?.config_json) {
          merged = deepMerge(merged, journeyConfigToBrand(journeyConfig.config_json as JourneyConfig));
        }

        // Update local cache
        setConfigs((prev) => ({
          ...prev,
          [scopeKey]: merged,
        }));

        return merged;
      } catch (error) {
        console.error('[useBrandConfig] Error loading config from database:', error);
        // Fallback to cached config or defaults
        if (cachedConfig !== undefined) {
          return deepMerge(merged, cachedConfig);
        }
        return merged;
      }
    },
    [configs]
  );

  /**
   * Save config for Hub context entities
   * Saves to database and refreshes compiled config
   * @returns The freshly compiled config after save
   */
  const saveConfigForContext = useCallback(
    async (
      operator: Operator | null,
      debtType: DebtType | null,
      client: Client | null,
      config: Partial<BrandConfiguration>
    ): Promise<BrandConfiguration> => {
      if (!operator) {
        console.warn('Cannot save config: no operator selected');
        return DEFAULT_BRAND_CONFIG;
      }

      try {
        const scope = getScopeType(debtType, client);

        // Extract domain-specific configs
        const themeConfig = extractThemeConfig(config);
        const contentConfig = extractContentConfig(config);
        const journeyConfig = extractJourneyConfig(config);

        // Load existing fragments to preserve fields managed by other pages
        const ownerId = configEntityId(debtType, client, operator.id);
        const [existingTheme, existingContent, existingJourney] = await Promise.all([
          Object.keys(themeConfig).length > 0 ? getRuleFragment(Domain.THEME, scope, ownerId) : null,
          Object.keys(contentConfig).length > 0 ? getRuleFragment(Domain.CONTENT, scope, ownerId) : null,
          Object.keys(journeyConfig).length > 0 ? getRuleFragment(Domain.JOURNEY, scope, ownerId) : null,
        ]);

        // Save all configs in parallel (merged with existing data)
        await Promise.all([
          Object.keys(themeConfig).length > 0
            ? saveConfig({
                domain: Domain.THEME,
                scope,
                rules: deepMerge((existingTheme?.rules as ThemeConfig) || {}, themeConfig),
                operatorId: operator.id,
                ...configScopeIds(debtType, client),
                notes: 'Brand configuration - Theme settings',
              })
            : Promise.resolve(),
          Object.keys(contentConfig).length > 0
            ? saveConfig({
                domain: Domain.CONTENT,
                scope,
                rules: deepMerge((existingContent?.rules as ContentConfig) || {}, contentConfig) as ContentConfig,
                operatorId: operator.id,
                ...configScopeIds(debtType, client),
                notes: 'Brand configuration - Content settings',
              })
            : Promise.resolve(),
          Object.keys(journeyConfig).length > 0
            ? saveConfig({
                domain: Domain.JOURNEY,
                scope,
                rules: deepMerge((existingJourney?.rules as JourneyConfig) || {}, journeyConfig) as JourneyConfig,
                operatorId: operator.id,
                ...configScopeIds(debtType, client),
                notes: 'Brand configuration - Journey settings',
              })
            : Promise.resolve(),
        ]);

        // Clear entire cache — changes may cascade to child scopes
        setConfigs({});

        // Refresh compiled config after save with cache bypass to avoid stale closure
        const refreshedConfig = await loadConfigForContext(operator, debtType, client, true);

        // Invalidate Library's brand config cache so it refetches on next mount
        queryClient.invalidateQueries({ queryKey: brandConfigKeys.all });

        return refreshedConfig;
      } catch (error) {
        console.error('Error saving configuration:', error);
        // Fallback to local state on error
        const scopeKey = buildScopeKey(operator.id, client?.id, debtType?.id);
        setConfigs((prev) => ({
          ...prev,
          [scopeKey]: config,
        }));
        return { ...DEFAULT_BRAND_CONFIG, ...config };
      }
    },
    [loadConfigForContext, queryClient]
  );

  return {
    loadConfigForContext,
    saveConfigForContext,
    configs,
  };
}
